import { HeardAboutAsmType } from '@/api/dtos';
import RegistrationField from '@/components/RegistrationField';
import { REGISTRATION_INFO_STEP_INDEX } from '@/constants/constants';
import { StepContext } from '@/contexts/StepContext';
import useRegistrationYear from '@/hooks/useRegistrationYear';
import { countryIsAustralia, requiresSpecialitySpecification, specifyDietaryRequirementCondition } from '@/utils/conditions';
import { convert, parse } from '@/utils/converters';
import { Grid, Paper, Typography } from '@mui/material';
import { Checkbox, Select, Text, ToggleButtonGroup } from '@ngt/forms';
import { Condition, ConditionMode } from '@ngt/forms-core';
import { FC } from 'react';

const requiresHeardAboutAsmSpecification = ({value}: any) => value?.heardAboutAsm == HeardAboutAsmType.Other;

const valueSubscription = {
    value: true
};

const RegistrationStepFour: FC = () => {
    const registrationYear = useRegistrationYear();
    const delegateListingLabel = `Do you agree to your name and contact details being included in the ${registrationYear} BCT ASM Delegate Listing?`;

    return (
        <StepContext.Provider value={REGISTRATION_INFO_STEP_INDEX}>
            <Paper
                sx={{ p: 4, pt: 0, mt: { xs: 1, md: 3 } }}
            >
                <Grid
                    container
                    columnSpacing={4}
                >
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={4}
                    >
                        <RegistrationField
                            label="Institution"
                            propertyPath="institution"
                            component={Text}
                            variant="standard"
                            
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={5}
                    >
                        <RegistrationField
                            label="Please tick if you are in your early/middle career in research"
                            propertyPath="earlyMidCareer"
                            component={Checkbox}
                            
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    columnSpacing={4}
                >
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={4}
                    >
                        <RegistrationField
                            label="Speciality"
                            propertyPath="speciality"
                            component={Select}
                            FormControlProps={{
                                variant: "standard"
                            }}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={4}
                    >                    
                        <Condition
                            condition={requiresSpecialitySpecification}
                            subscription={valueSubscription}
                            mode={ConditionMode.Show}
                        >
                            <RegistrationField
                                label="Specify Other"
                                propertyPath="specialitySpec"
                                component={Text}
                                variant="standard"
                            />
                        </Condition>
                    </Grid>
                </Grid>
                <Grid
                    container
                    columnSpacing={4}
                >
                    <Grid
                        item
                        xs={12}
                        md={12}
                        mt={4}
                    >
                        <RegistrationField
                            label="Address"
                            propertyPath="address"
                            component={Text}
                            variant="standard"
                            
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    columnSpacing={4}
                >
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={4}
                    >
                        <RegistrationField
                            label="Suburb"
                            propertyPath="suburb"
                            component={Text}
                            variant="standard"
                            
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={4}
                    >
                        <RegistrationField
                            label="Postcode"
                            propertyPath="postcode"
                            component={Text}
                            variant="standard"
                            
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    columnSpacing={4}
                >
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={4}
                    >
                        <RegistrationField
                            label="Country"
                            propertyPath="country"
                            component={Text}
                            variant="standard"
                            
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={4}
                    >
                        <Condition
                            condition={countryIsAustralia}
                            subscription={valueSubscription}
                            mode={ConditionMode.Enable}
                        >
                            <RegistrationField
                                label="State / Territory"
                                propertyPath="state"
                                component={Select}
                                FormControlProps={{
                                    variant: "standard"
                                }}
                                
                            />
                        </Condition>
                    </Grid>
                </Grid>
                <Grid
                    container
                    columnSpacing={4}
                >
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={4}
                    >
                        <RegistrationField
                            label="Mobile Phone Number"
                            propertyPath="mobile"
                            component={Text}
                            variant="standard"
                            
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={4}
                    >
                        <RegistrationField
                            label="Email"
                            propertyPath="email"
                            component={Text}
                            variant="standard"
                            
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    columnSpacing={4}
                >
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={4}
                    >
                        <RegistrationField
                            label="Where did you hear about the BCT ASM?"
                            propertyPath="heardAboutAsm"
                            component={Select}
                            FormControlProps={{
                                variant: "standard"
                            }}
                            
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={4}
                    >                    
                        <Condition
                            condition={requiresHeardAboutAsmSpecification}
                            subscription={valueSubscription}
                            mode={ConditionMode.Show}
                        >
                            <RegistrationField
                                label="Specify Other"
                                propertyPath="heardAboutAsmOther"
                                component={Text}
                                variant="standard"
                            />
                        </Condition>
                    </Grid>
                </Grid>
                <Grid
                    container
                    columnSpacing={4}
                    
                >
                    <Grid 
                        item 
                        xs={12}
                        md={6}
                        mt={4}
                    >
                        <RegistrationField
                            label="Dietary Requirement"
                            propertyPath="dietaryRequirement"
                            component={Select}
                            FormControlProps={{
                                variant: "standard"
                            }}
                        />
                    </Grid>
                    <Grid 
                        item 
                        xs={12}
                        md={6}
                        mt={2}
                    >                   
                        <Condition
                            condition={specifyDietaryRequirementCondition}
                            subscription={valueSubscription}
                            mode={ConditionMode.Enable}
                        >
                            <RegistrationField
                                label="Please enter any dietary requirements, including for any additional attendees"
                                propertyPath="dietaryRequirements"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Condition>
                    </Grid>
                </Grid>
                <Grid
                    container
                    columnSpacing={4}
                    mt={4}
                >
                    <Grid item xs={12} md={8}><Typography>Do you require any disability access to the event?</Typography></Grid>
                    <Grid item xs={12} md={4} sx={{ mt: { xs: 2, md: 0 }}}>
                        <RegistrationField
                            label="Do you require any disability access to the event?"
                            propertyPath="requiresDisabilityAccess"
                            component={ToggleButtonGroup}
                            sx={{margin:"normal"}}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    columnSpacing={4}
                    mt={4}
                >
                    <Grid item xs={12} md={8}><Typography>Do you require any visual or audio assistance at the event?</Typography></Grid>
                    <Grid item xs={12} md={4} sx={{ mt: { xs: 2, md: 0 }}}>
                        <RegistrationField
                            label="Do you require any visual or audio assistance at the event?"
                            propertyPath="requiresAVAssistance"
                            component={ToggleButtonGroup}
                            sx={{margin:"normal"}}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    columnSpacing={4}
                    mt={4}
                >
                    <Grid item xs={12} md={8}><Typography>{delegateListingLabel}</Typography></Grid>
                    <Grid item xs={12} md={4} sx={{ mt: { xs: 2, md: 0 }}}>
                        <RegistrationField
                            label={delegateListingLabel}
                            propertyPath="includeDetails"
                            component={ToggleButtonGroup}
                            sx={{margin:"normal"}}
                            parse={parse}
                            convert={convert}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    columnSpacing={4}
                    mt={4}
                >
                    <Grid item xs={12} md={8}><Typography>Would you like to receive a certificate of attendance?</Typography></Grid>
                    <Grid item xs={12} md={4} sx={{ mt: { xs: 2, md: 0 }}}>
                        <RegistrationField
                            label="Would you like to receive a certificate of attendance?"
                            propertyPath="receiveCertificate"
                            component={ToggleButtonGroup}
                            sx={{margin:"normal"}}
                            parse={parse}
                            convert={convert}
                        />
                    </Grid>
                </Grid>
            </Paper>
        </StepContext.Provider>
    )
}

export default RegistrationStepFour;